/**
 * RequirementRow — unified expandable row for "requirement vs candidate" UX.
 *
 * Used by:
 *   - JobFamilyPage Levels tab (target = level's bar, has = candidate's bar)
 *   - JobPostingPreviewPage requirements tab (target = posting's bar, has = candidate's bar)
 *
 * Closed row: [optional category chip] [text] [target dots] [chevron]
 * Expanded:   "How companies word this" (if wordings) +
 *             "Your evidence" (or empty state) +
 *             "I have this" / "I don't have this" CTAs (if showCtas)
 *
 * The target-dot model: each dot = a notch on a 5-point bar.
 *   - dot has both ring + inner fill  → "target" (required AND you have it)
 *   - dot has ring only               → "gap"    (required, but you don't have it)
 *   - dot has inner fill only         → "exceeds" (you have it, beyond what's asked)
 */
import React from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { SAGE, CREAM } from '../../theme';

const F = "'Outfit', sans-serif";

const MET_COLOR = '#3d7a4a';
const GAP_COLOR = '#c4956a';

const TARGET_DOT_TITLES = {
  target: 'Required and you have it',
  gap: "Required, but we haven't found this in your profile",
  exceeds: "You have this — role doesn't require it",
};

const TargetDot = ({ kind }) => {
  const color = kind === 'gap' ? GAP_COLOR : MET_COLOR;
  const showRing = kind === 'target' || kind === 'gap';
  const showInner = kind === 'target' || kind === 'exceeds';
  return (
    <Box
      title={TARGET_DOT_TITLES[kind]}
      sx={{ position: 'relative', width: 14, height: 14, flexShrink: 0 }}
    >
      {showRing && (
        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `1.5px solid ${color}` }} />
      )}
      {showInner && (
        <Box
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: color, transform: 'translate(-50%, -50%)',
          }}
        />
      )}
    </Box>
  );
};

const TargetDots = ({ required, has }) => {
  const total = Math.max(required || 0, has || 0, 1);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.55 }}>
      {Array.from({ length: total }).map((_, i) => {
        const isReq = i < (required || 0);
        const hasIt = i < (has || 0);
        const kind = isReq && hasIt ? 'target' : isReq ? 'gap' : 'exceeds';
        return <TargetDot key={i} kind={kind} />;
      })}
    </Box>
  );
};

const STRENGTH_COLORS = {
  strong: '#3d7a4a',
  medium: '#c4956a',
  weak: '#c48a8a',
};

const StrengthDot = ({ strength }) => (
  <Box
    sx={{
      width: 7,
      height: 7,
      borderRadius: '50%',
      bgcolor: STRENGTH_COLORS[strength] || SAGE[300],
      flexShrink: 0,
      mt: 0.6,
    }}
    title={`${strength} match`}
  />
);

const EvidenceList = ({ items }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    {items.map((ev, i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <StrengthDot strength={ev.strength} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.82rem', color: SAGE[700], lineHeight: 1.55, fontFamily: F }}>
            {ev.text}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: SAGE[400], mt: 0.25, fontFamily: F }}>
            {ev.source}{ev.dates ? ` · ${ev.dates}` : ''}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const CATEGORY_COLORS = {
  skill:       { bg: '#e8ebe5', color: '#435a37' },
  experience:  { bg: '#f5f0e8', color: '#8b6f3e' },
  education:   { bg: '#e8e3d8', color: '#6b5b3a' },
  soft:        { bg: '#f0e8e3', color: '#8a5a4a' },
  domain:      { bg: '#e3e8eb', color: '#3a5a6b' },
};

const FrequencyBar = ({ frequency }) => {
  const pct = Math.round((frequency || 0) * 100);
  return (
    <Box sx={{ width: 140, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: SAGE[100], overflow: 'hidden' }}>
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            bgcolor: pct > 75 ? SAGE[500] : pct > 50 ? SAGE[400] : SAGE[300],
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          color: SAGE[600],
          fontFamily: F,
          width: 36,
          textAlign: 'right',
        }}
      >
        {pct}%
      </Typography>
    </Box>
  );
};

const RequirementRow = ({
  text,
  category,           // optional - hides chip if absent
  target,             // 1..5 — role's expected level (optional)
  has,                // 1..5 — candidate's level (optional, defaults to 0)
  frequency,          // 0..1 — fallback when target/has aren't curated yet
  wordings,           // optional string[] - "How companies word this"
  evidence,           // optional Evidence[] - profile evidence; if absent shows empty state
  expanded,
  onToggle,
  onIHaveThis,        // optional — called when user clicks "I have this"
  onIDontHaveThis,    // optional — called when user clicks "I don't have this"
  startingConversation = false,
  showCtas = true,    // both CTAs visible in expanded state
  index,              // optional 0-based — shown as a numbered badge if present
}) => {
  const cat = category && CATEGORY_COLORS[category];
  const hasValue = has != null ? has : 0;
  const useTargetDots = target != null;
  const rightSlot = useTargetDots ? (
    <Tooltip
      title={(hasValue >= target)
        ? `You meet the bar (${hasValue}/${target})`
        : `Role asks for ${target}, profile shows ${hasValue}`}
      placement="top"
    >
      <Box sx={{ display: 'inline-flex' }}>
        <TargetDots required={target} has={hasValue} />
      </Box>
    </Tooltip>
  ) : frequency != null ? (
    <FrequencyBar frequency={frequency} />
  ) : null;

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${SAGE[100]}`,
        '&:last-of-type': { borderBottom: 'none' },
        bgcolor: expanded ? `${CREAM[100]}80` : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2.5, py: 1.5, cursor: 'pointer',
          '&:hover': { bgcolor: `${SAGE[50]}80` },
        }}
      >
        {index != null && (
          <Box
            sx={{
              minWidth: 22, height: 22, borderRadius: 1,
              bgcolor: SAGE[100], color: SAGE[600],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: F, fontSize: '0.7rem', fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {index + 1}
          </Box>
        )}
        {cat && (
          <Box
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: cat.bg,
              color: cat.color,
              fontFamily: F,
              width: 80,
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            {category}
          </Box>
        )}
        <Typography sx={{ flex: 1, fontSize: '0.88rem', color: SAGE[800], lineHeight: 1.55, fontFamily: F, fontWeight: 500 }}>
          {text}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
          {rightSlot}
          <ExpandMoreIcon
            sx={{
              color: SAGE[400],
              fontSize: 20,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.15s',
            }}
          />
        </Box>
      </Box>

      {expanded && (
        <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5, ml: index != null ? 4.5 : 0, borderLeft: `2px solid ${SAGE[300]}55`, mr: 2.5 }}>
          {wordings && wordings.length > 0 && (
            <Box sx={{ ml: 1.5, mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: SAGE[500],
                  fontFamily: F,
                  mb: 1.25,
                }}
              >
                How companies word this
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {wordings.map((w, j) => (
                  <Typography
                    key={j}
                    sx={{
                      fontSize: '0.8rem',
                      lineHeight: 1.55,
                      color: SAGE[500],
                      fontStyle: 'italic',
                      fontFamily: F,
                    }}
                  >
                    "{w}"
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ ml: 1.5 }}>
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: SAGE[500],
                fontFamily: F,
                mb: 1.25,
              }}
            >
              {evidence && evidence.length > 0 ? 'Your evidence' : 'No evidence in your profile yet'}
            </Typography>
            {evidence && evidence.length > 0 ? (
              <EvidenceList items={evidence} />
            ) : (
              <Typography sx={{ fontSize: '0.78rem', color: SAGE[500], fontStyle: 'italic', fontFamily: F }}>
                We didn't find anything in your profile that points to this. If it's buried in your background, let's surface it together — or plan a path to build it.
              </Typography>
            )}
          </Box>

          {showCtas && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, ml: 1.5 }}>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  if (!startingConversation && onIHaveThis) onIHaveThis();
                }}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75,
                  px: 1.75, py: 0.85, borderRadius: 1.5,
                  bgcolor: startingConversation ? SAGE[300] : SAGE[500],
                  color: CREAM[50],
                  cursor: startingConversation ? 'default' : 'pointer',
                  fontSize: '0.74rem', fontWeight: 600,
                  fontFamily: F,
                  '&:hover': { bgcolor: startingConversation ? SAGE[300] : SAGE[600] },
                }}
              >
                {startingConversation ? (
                  <CircularProgress size={12} sx={{ color: CREAM[50] }} />
                ) : (
                  <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                )}
                I have this — let's talk about it
              </Box>
              {onIDontHaveThis ? (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onIDontHaveThis();
                  }}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                    px: 1.75, py: 0.85, borderRadius: 1.5,
                    bgcolor: 'transparent',
                    color: SAGE[600],
                    border: `1.5px solid ${SAGE[300]}`,
                    cursor: 'pointer',
                    fontSize: '0.74rem', fontWeight: 600,
                    fontFamily: F,
                    transition: 'all 0.18s',
                    '&:hover': { bgcolor: `${SAGE[200]}55`, borderColor: SAGE[500], color: SAGE[800] },
                  }}
                >
                  <LightbulbOutlinedIcon sx={{ fontSize: 14 }} />
                  I don't have this — how do I fill it?
                </Box>
              ) : (
                <Tooltip title="Coming soon — AI-suggested ways to close the gap" placement="top">
                  <Box
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.75,
                      px: 1.75, py: 0.85, borderRadius: 1.5,
                      bgcolor: 'transparent',
                      color: SAGE[400],
                      border: `1.5px solid ${SAGE[200]}`,
                      cursor: 'not-allowed',
                      fontSize: '0.74rem', fontWeight: 600,
                      fontFamily: F,
                    }}
                  >
                    <LightbulbOutlinedIcon sx={{ fontSize: 14 }} />
                    I don't have this — how do I fill it?
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default RequirementRow;
export { TargetDots, TargetDot };
